<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { runtimeConfig } from '@/config/runtime.js'
import { authStore } from '@/stores/auth.js'
import { workspaceIdentityForRole, workspaceIdentityForTarget } from '@/workspaceIdentity.js'

const route = useRoute()
const router = useRouter()
let sessionSyncRequest = null
const usesWorkspaceChrome = computed(() => route.matched.some((record) => record.meta.workspaceChrome))
const workspaceIdentity = computed(() => route.meta.loginWorkspace
  ? workspaceIdentityForRole(route.meta.loginWorkspace)
  : workspaceIdentityForTarget(route.path))
const loginHome = computed(() => {
  if (route.meta.loginWorkspace === 'superadmin' || route.path.startsWith('/superadmin')) return '/login/superadmin'
  if (route.meta.loginWorkspace === 'enterprise_admin' || route.path.startsWith('/enterprise-admin')) return '/login/enterprise-admin'
  return '/login'
})

async function syncVisibleTabSession() {
  if (sessionSyncRequest) return sessionSyncRequest
  sessionSyncRequest = (async () => {
    try {
      const result = await authStore.revalidateSession()
      if (!result.changed) return
      const target = await authStore.prepareDefaultTenant()
      if (target !== route.path) await router.replace(target)
    } catch {
      // A temporary network failure must not discard a valid visible page.
      // The next focus/visibility event retries authoritative session lookup.
    }
  })().finally(() => { sessionSyncRequest = null })
  return sessionSyncRequest
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') syncVisibleTabSession()
}

onMounted(() => {
  window.addEventListener('focus', syncVisibleTabSession)
  window.addEventListener('pm:session-replaced', syncVisibleTabSession)
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  window.removeEventListener('focus', syncVisibleTabSession)
  window.removeEventListener('pm:session-replaced', syncVisibleTabSession)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <RouterView v-if="usesWorkspaceChrome" />
  <div v-else class="app-frame" :class="workspaceIdentity.key">
    <header class="topbar">
      <RouterLink class="brand" :to="{ path: loginHome, query: route.query.next ? { next: route.query.next } : {} }" :aria-label="`${workspaceIdentity.workspace}登录首页`">
        <span class="brand-mark" aria-hidden="true">{{ workspaceIdentity.mark }}</span>
        <span class="brand-copy">
          <strong>{{ workspaceIdentity.workspace }}</strong>
          <small>{{ workspaceIdentity.level }} · {{ workspaceIdentity.name }}</small>
        </span>
      </RouterLink>
      <span class="cloud-state">
        <span class="cloud-state-dot" aria-hidden="true"></span>
        云端服务
      </span>
    </header>

    <main class="main-content">
      <RouterView />
    </main>

    <footer class="footer">
      <span>数据与任务在云端持续运行</span>
      <span class="runtime-mode">{{ runtimeConfig.apiMode === 'same-origin' ? '同源安全连接' : 'HTTPS 安全连接' }}</span>
    </footer>
  </div>
</template>
